/**
 * What a свиня and a тур are made of, and what they are painted with.
 *
 * The same machinery as the cossack skins - boxes unwrapped onto one texture,
 * a shelf packer, and a painter working in texture pixels - but a different
 * body plan: a quadruped is a long box on four short ones, with the head hung
 * off the front rather than stacked on top.
 *
 * Both animals are drawn from the photographs: the pig long, pink and
 * low-slung with a snout and drooping ears; the тур dark red-brown, taller at
 * the shoulder, with the pale forward-curving horns of the wild bull this
 * country had before anyone fenced anything.
 *
 * Pure pixels and rectangles. No engine, no DOM - the renderer reads the
 * layout to place its boxes and the painter to fill the texture, and neither
 * of them can disagree about where a face is because both ask this file.
 */

import {
  SKIN_TEX, unwrapBox, type BoxFaces, type BoxSize, type Rect,
} from "../player/skins.js";
import type { BeastKind } from "./beast.js";

export type BeastPart =
  | "body" | "head" | "muzzle" | "tail"
  | "legFR" | "legFL" | "legBR" | "legBL"
  | "hornR" | "hornL"
  | "wingR" | "wingL" | "crest" | "neck"
  /*
   * And a Змій Горинич's other two of everything.
   *
   * Three heads on three necks is the only thing about him that the body
   * plan here could not already say. Everything else he has - wings, horns,
   * a tail, four legs - was here for a hen or a тур. The side heads are the
   * same boxes as the middle one and are painted by the same code; what they
   * need is somewhere of their own on the texture, because two boxes sharing
   * a rectangle is two boxes that can never differ, and these do: the outer
   * two look outwards.
   */
  | "headR" | "headL" | "muzzleR" | "muzzleL" | "neckR" | "neckL";

export type BeastLayout = Partial<Record<BeastPart, BoxFaces>>;

export interface BeastParts {
  /** w across the animal, h its depth of body, d its length nose to tail. */
  readonly body: BoxSize;
  readonly head: BoxSize;
  /** A snout on the beasts, a beak on the bird. */
  readonly muzzle: BoxSize;
  readonly leg: BoxSize;
  /** An ear on a pig, a horn on a тур, the гребінь on a курка. */
  readonly horn: BoxSize;
  readonly tail: BoxSize;
  /** A bird has these; a beast leaves them out. */
  readonly wing?: BoxSize;
  /** The чуб of the українська чубата. Nothing else has one. */
  readonly crest?: BoxSize;
  /**
   * The bird's neck.
   *
   * A beast's head hangs straight off the front of its body and needs
   * nothing between; a hen holds hers up on a neck, and without one the head
   * and the body run together into a single brown column with a beak on it.
   * That is what the first one looked like.
   */
  readonly neck?: BoxSize;
  /**
   * Carried up off the rump rather than hanging.
   *
   * A hen's tail is up because she is a bird and the model already knew
   * that; a dog's and a cat's are up because that is how a dog and a cat
   * carry them, and a tail hanging straight down the back of a small animal
   * disappears into its own legs. Written as a property of the animal rather
   * than a second rule about stances, because it is about this animal.
   */
  readonly tailUp?: boolean;
  /** Shoulder height in skin pixels: legs plus body. Sets the scale. */
  readonly standing: number;
  /** Three, on a Змій Горинич. One on everything else. */
  readonly heads?: 1 | 3;
}

export interface BeastPalette {
  readonly hide: number;
  readonly hideLit: number;
  readonly hideDark: number;
  /** Underside, which catches no light and is paler on every animal here. */
  readonly belly: number;
  readonly muzzle: number;
  readonly hoof: number;
  readonly horn: number;
}

export interface BeastSkinDef {
  readonly kind: BeastKind;
  readonly parts: BeastParts;
  readonly palette: BeastPalette;
  /** Ears lie flat against the head; horns stand out from it. */
  readonly headgear: "ears" | "horns" | "comb";
  /**
   * Four legs and a head hung off the front, or two legs and a head on top.
   *
   * The difference is the whole of what separates a bird from a beast here,
   * and both the model and the painter ask this rather than asking which
   * species it is - so a second bird would need no new code in either.
   */
  readonly stance: "beast" | "bird" | "dragon";
  /** A Змій has two more heads on two more necks. Nothing else does. */
  readonly heads?: 1 | 3;
}

/**
 * The pig: fourteen pixels of body on five of leg.
 *
 * Long and low is the whole silhouette - the photograph is an animal much
 * longer than it is tall, and a pig drawn square reads as a dog.
 */
export const PIG_SKIN: BeastSkinDef = {
  kind: "pig",
  parts: {
    body: { w: 8, h: 8, d: 14 },
    head: { w: 7, h: 6, d: 5 },
    muzzle: { w: 4, h: 3, d: 2 },
    leg: { w: 3, h: 5, d: 3 },
    horn: { w: 3, h: 3, d: 1 },
    tail: { w: 1, h: 3, d: 1 },
    standing: 13,
  },
  palette: {
    hide: 0xe8b3b0,
    hideLit: 0xf6cdc9,
    hideDark: 0xc98e8d,
    belly: 0xf0c4bf,
    muzzle: 0xd98f96,
    hoof: 0x5b4740,
    horn: 0xe8b3b0,
  },
  headgear: "ears",
  stance: "beast",
};

/** The тур: taller at the shoulder, heavier through the neck, and horned. */
export const TUR_SKIN: BeastSkinDef = {
  kind: "tur",
  parts: {
    body: { w: 10, h: 11, d: 18 },
    head: { w: 8, h: 8, d: 7 },
    muzzle: { w: 5, h: 4, d: 3 },
    leg: { w: 4, h: 9, d: 4 },
    horn: { w: 7, h: 2, d: 2 },
    tail: { w: 1, h: 7, d: 1 },
    standing: 20,
  },
  palette: {
    hide: 0x7a3f26,
    hideLit: 0x9c5834,
    hideDark: 0x4e2717,
    belly: 0x8d4f2c,
    muzzle: 0x33211a,
    hoof: 0x2a201c,
    horn: 0xd9cfb4,
  },
  headgear: "horns",
  stance: "beast",
};

/**
 * Курка: a полтавська глиняста with the чубата's tuft.
 *
 * Clay through the body - light warm brown, which is what «глиняста» means -
 * with the neck a shade lighter, near-black on the tips of the wing and tail
 * feathers, a yellow beak and yellow legs, and a red гребінь tipped sideways
 * by the чуб. Nothing about it is the white bird every other game draws, and
 * that is the point: this one came out of a photograph of a Ukrainian yard.
 *
 * Seven pixels of body on four of leg, and it stands upright rather than
 * lying along its own length - the silhouette of a hen is a wedge on stilts,
 * and one drawn long and low would read as a duck.
 */
export const KURKA_SKIN: BeastSkinDef = {
  kind: "kurka",
  parts: {
    body: { w: 6, h: 7, d: 8 },
    head: { w: 4, h: 5, d: 4 },
    muzzle: { w: 2, h: 2, d: 3 },
    leg: { w: 2, h: 4, d: 2 },
    horn: { w: 1, h: 3, d: 4 },
    tail: { w: 1, h: 6, d: 2 },
    wing: { w: 1, h: 4, d: 6 },
    crest: { w: 3, h: 2, d: 3 },
    neck: { w: 3, h: 4, d: 3 },
    /*
     * Legs plus body plus head, because a bird carries its head on top.
     *
     * On a beast this is legs plus body and the head hangs off the front, so
     * it changes nothing. On a hen the head is the top of her: counting only
     * legs and body made her a fifth taller than the thing that collides
     * with the world thought she was, and her comb went through ceilings.
     */
    standing: 16,
  },
  palette: {
    hide: 0xc08a4e,
    hideLit: 0xdcab6c,
    hideDark: 0x241c14,
    belly: 0xe0c48d,
    muzzle: 0xe8b435,
    hoof: 0xe0a72c,
    horn: 0xc4242a,
  },
  headgear: "comb",
  stance: "bird",
};

/**
 * Собака: the дворняга that lies by every хата in the country.
 *
 * Deliberately not a breed. Ukrainian yard dogs are middling, rough and
 * sandy, with a dark saddle and a curl of tail held high - so the silhouette
 * is a body a little longer than the pig's is deep, on longer legs, with a
 * tail that stands up rather than hanging. Ears, not horns.
 *
 * Twelve pixels at the shoulder against the pig's thirteen: a dog is smaller
 * than a pig and stands higher off the ground, which is most of what stops
 * the two reading as the same animal in two colours.
 */
export const SOBAKA_SKIN: BeastSkinDef = {
  kind: "sobaka",
  parts: {
    body: { w: 5, h: 6, d: 10 },
    head: { w: 5, h: 5, d: 5 },
    muzzle: { w: 3, h: 3, d: 3 },
    leg: { w: 2, h: 5, d: 2 },
    horn: { w: 2, h: 4, d: 2 },
    tail: { w: 2, h: 5, d: 2 },
    tailUp: true,
    standing: 11,
  },
  palette: {
    hide: 0xbf8f4f,
    hideLit: 0xd8ab68,
    hideDark: 0x8a6335,
    belly: 0xe0c088,
    // A dark muzzle, not a black one. At this size near-black on a small
    // head is not a snout, it is a hole in the animal.
    muzzle: 0x74512c,
    hoof: 0x4a3624,
    horn: 0x8a6335,
  },
  headgear: "ears",
  stance: "beast",
};

/**
 * Кіт: smaller than everything, and shaped like nothing else here.
 *
 * A grey tabby, which is what a village cat is. The one thing that has to
 * come through at this size is the proportion - a short body on short legs
 * with a long tail - because a cat drawn like a small dog is a small dog.
 * Eight pixels at the shoulder: half a тур's leg.
 */
export const KIT_SKIN: BeastSkinDef = {
  kind: "kit",
  parts: {
    body: { w: 4, h: 4, d: 8 },
    head: { w: 4, h: 4, d: 4 },
    muzzle: { w: 2, h: 2, d: 2 },
    leg: { w: 2, h: 4, d: 2 },
    horn: { w: 2, h: 2, d: 1 },
    tail: { w: 2, h: 6, d: 2 },
    tailUp: true,
    standing: 8,
  },
  palette: {
    hide: 0x8a8480,
    hideLit: 0xa8a29c,
    hideDark: 0x55504c,
    belly: 0xc4beb6,
    muzzle: 0xd8b4a8,
    hoof: 0x55504c,
    horn: 0x55504c,
  },
  headgear: "ears",
  stance: "beast",
};

/**
 * Змій Горинич: three heads on three necks, wings, and a tail as long as he
 * is.
 *
 * Read off the character sheet rather than invented. He is "12-15 m long,
 * 6-8 m tall" against a man's 1.8, which in this game's pixels - a cossack
 * is 32 tall and 1.8 blocks - makes him about four blocks at the shoulder
 * and eight from nose to tail. Everything else follows from that: a body
 * longer than the тур is tall, legs like tree trunks, and a wingspan wider
 * than the whole animal.
 *
 * The outer two heads are the middle one twice over, hung out to each side
 * and turned outwards. That is what the drawing has, and it is why three
 * heads needed nothing new here except somewhere to put them: a head is a
 * head.
 */
/*
 * Every box he has, trimmed until they fit.
 *
 * A hundred and twenty-eight pixels square is the whole texture, shared with
 * the cossacks, and the Змій is the first animal with enough parts to run
 * out of it: three heads, three necks, three muzzles, two wings, four legs,
 * a body and a tail. The first set of sizes needed 131 rows of a sheet 128
 * tall, which `check` caught before anything was ever rendered - and a part
 * packed off the edge does not fail, it draws whatever is at the top of the
 * sheet.
 */
export const ZMII_SKIN: BeastSkinDef = {
  kind: "zmii",
  parts: {
    body: { w: 14, h: 9, d: 20 },
    head: { w: 7, h: 7, d: 9 },
    muzzle: { w: 5, h: 4, d: 7 },
    leg: { w: 6, h: 8, d: 6 },
    horn: { w: 2, h: 5, d: 2 },
    tail: { w: 5, h: 5, d: 16 },
    wing: { w: 14, h: 2, d: 20 },
    neck: { w: 5, h: 5, d: 11 },
    heads: 3,
    tailUp: false,
    standing: 17,
  },
  palette: {
    hide: 0x3f6b34,
    hideLit: 0x568a41,
    hideDark: 0x2a4a22,
    belly: 0x9aa855,
    muzzle: 0x7a2420,
    hoof: 0x2a2118,
    horn: 0xc8b48a,
  },
  headgear: "horns",
  stance: "dragon",
  heads: 3,
};

export const BEAST_SKINS: Readonly<Record<BeastKind, BeastSkinDef>> = {
  pig: PIG_SKIN,
  tur: TUR_SKIN,
  kurka: KURKA_SKIN,
  sobaka: SOBAKA_SKIN,
  kit: KIT_SKIN,
  zmii: ZMII_SKIN,
};

/**
 * Pack every part onto the texture, left to right, wrapping to a new shelf.
 *
 * Same packer as the cossacks, and deterministic for a given set of parts, so
 * the painter and the UV mapper are always looking at the same rectangles.
 */
export function layoutBeast(p: BeastParts): BeastLayout {
  const parts: [BeastPart, BoxSize][] = [
    ["body", p.body],
    ["head", p.head],
    ["muzzle", p.muzzle],
    ["legFR", p.leg], ["legFL", p.leg], ["legBR", p.leg], ["legBL", p.leg],
    ["hornR", p.horn], ["hornL", p.horn],
    ["tail", p.tail],
  ];
  // A bird's, packed after the rest so adding them moved nothing: the packer
  // is deterministic, and the two beasts have to come out of it unchanged.
  if (p.wing !== undefined) parts.push(["wingR", p.wing], ["wingL", p.wing]);
  if (p.crest !== undefined) parts.push(["crest", p.crest]);
  if (p.neck !== undefined) parts.push(["neck", p.neck]);
  // The Змій's other two heads, packed last for the same reason the wings
  // were: the packer is deterministic and nothing already on the texture may
  // move because a new animal arrived.
  if (p.heads === 3) {
    parts.push(["headR", p.head], ["headL", p.head]);
    parts.push(["muzzleR", p.muzzle], ["muzzleL", p.muzzle]);
    if (p.neck !== undefined) parts.push(["neckR", p.neck], ["neckL", p.neck]);
  }

  const layout: BeastLayout = {};
  let x = 0;
  let y = 0;
  let shelf = 0;

  for (const [name, size] of parts) {
    const width = 2 * size.d + 2 * size.w;
    const height = size.d + size.h;
    if (x + width > SKIN_TEX) {
      x = 0;
      y += shelf;
      shelf = 0;
    }
    layout[name] = unwrapBox(x, y, size);
    x += width;
    shelf = Math.max(shelf, height);
  }
  return layout;
}

/** Does the whole animal fit on the texture? */
export function beastLayoutFits(layout: BeastLayout): boolean {
  for (const faces of Object.values(layout)) {
    const b = faces.bounds;
    if (b.x + b.w > SKIN_TEX || b.y + b.h > SKIN_TEX) return false;
  }
  return true;
}

function put(px: Uint8ClampedArray, x: number, y: number, color: number): void {
  if (x < 0 || y < 0 || x >= SKIN_TEX || y >= SKIN_TEX) return;
  const i = (y * SKIN_TEX + x) * 4;
  px[i] = (color >> 16) & 0xff;
  px[i + 1] = (color >> 8) & 0xff;
  px[i + 2] = color & 0xff;
  px[i + 3] = 255;
}

function fill(px: Uint8ClampedArray, r: Rect, color: number): void {
  for (let y = 0; y < r.h; y++) {
    for (let x = 0; x < r.w; x++) put(px, r.x + x, r.y + y, color);
  }
}

/** Every face of one box in one colour, as a base to draw detail over. */
function fillBox(px: Uint8ClampedArray, faces: BoxFaces, color: number): void {
  for (const key of ["top", "bottom", "right", "left", "front", "back"] as const) {
    fill(px, faces[key], color);
  }
}

/**
 * A scatter of darker pixels, so a flank is hide rather than a painted panel.
 *
 * Hashed off the pixel's own position, not random: a skin has to come out the
 * same every time it is painted, or two animals of one species would differ
 * and the texture could not be cached.
 */
function speckle(px: Uint8ClampedArray, r: Rect, color: number, chance: number): void {
  for (let y = 0; y < r.h; y++) {
    for (let x = 0; x < r.w; x++) {
      const h = Math.sin((r.x + x) * 12.9898 + (r.y + y) * 78.233) * 43758.5453;
      if (h - Math.floor(h) < chance) put(px, r.x + x, r.y + y, color);
    }
  }
}

/**
 * Paint one animal onto a fresh texture.
 *
 * @returns RGBA bytes, SKIN_TEX square, ready to upload.
 */
export function paintBeast(def: BeastSkinDef): Uint8ClampedArray {
  const px = new Uint8ClampedArray(SKIN_TEX * SKIN_TEX * 4);
  const layout = layoutBeast(def.parts);
  const pal = def.palette;

  const body = layout.body;
  if (body !== undefined) {
    fillBox(px, body, pal.hide);
    // Light along the spine, the underside paler - which is true of both
    // animals in the photographs and is most of what stops a box reading as
    // a box.
    fill(px, body.top, pal.hideLit);
    fill(px, body.bottom, pal.belly);
    for (const flank of [body.right, body.left]) {
      speckle(px, flank, pal.hideDark, 0.14);
      // A band of belly along the bottom of each flank, so the pale underside
      // wraps round rather than stopping dead at the edge.
      fill(px, {
        x: flank.x, y: flank.y + flank.h - 2, w: flank.w, h: 2,
      }, pal.belly);
    }
    speckle(px, body.top, pal.hide, 0.2);
  }

  /*
   * Every head this animal has, painted by the same code.
   *
   * A loop rather than three copies, because the Змій's outer heads are his
   * middle one twice over - and a face drawn three times by three pieces of
   * code is a face that ends up three different faces.
   */
  for (const head of [layout.head, layout.headR, layout.headL]) {
    if (head === undefined) continue;
    fillBox(px, head, pal.hide);
    fill(px, head.top, pal.hideLit);
    fill(px, head.bottom, pal.belly);
    // The face is the -Z face, which is the front of the model.
    const f = head.front;
    const eyeY = f.y + Math.max(1, Math.round(f.h * 0.3));
    /*
     * Hard against the sides of a narrow face.
     *
     * A fixed inset of one pixel puts both eyes of a four-wide face in the
     * middle two columns, where they touch and read as a single black bar
     * across the bird's head. The beasts' faces are seven and eight wide and
     * are unaffected.
     */
    const inset = f.w <= 5 ? 0 : Math.max(1, Math.round(f.w * 0.15));
    for (const ex of [f.x + inset, f.x + f.w - inset - 1]) {
      put(px, ex, eyeY, 0xf2ece0);
      put(px, ex, eyeY + 1, 0x1a1410);
    }
    /*
     * Сережки: the red wattles under a hen's beak.
     *
     * Two pixels, and they are half of what makes a small brown box read as
     * poultry rather than as a sparrow - the other half is the гребінь on
     * top. A hen's head is mostly punctuation.
     */
    if (def.headgear === "comb") {
      put(px, f.x + Math.floor(f.w / 2) - 1, f.y + f.h - 1, pal.horn);
      put(px, f.x + Math.floor(f.w / 2), f.y + f.h - 1, pal.horn);
      // And the neck a shade lighter than the body, as the глиняста's is.
      for (const cheek of [head.right, head.left]) {
        fill(px, {
          x: cheek.x, y: cheek.y + cheek.h - 1, w: cheek.w, h: 1,
        }, pal.belly);
      }
    }
    // A blaze down the forehead on the тур, which every drawing of one has.
    if (def.headgear === "horns") {
      fill(px, { x: f.x + Math.floor(f.w / 2) - 1, y: f.y, w: 2, h: 2 }, pal.hideDark);
      speckle(px, head.right, pal.hideDark, 0.12);
      speckle(px, head.left, pal.hideDark, 0.12);
    }
  }

  for (const muzzle of [layout.muzzle, layout.muzzleR, layout.muzzleL]) {
    if (muzzle === undefined) continue;
    fillBox(px, muzzle, pal.muzzle);
    /*
     * Two nostrils on the front face. On a pig this is the whole snout and
     * the single most recognisable thing about the animal.
     *
     * Not on a beak, which is two pixels wide: the two nostrils are then the
     * two pixels, and the bird's whole beak comes out black. It joined up
     * with the eyes into one bar across her face - which is what a лелека
     * looks like, and this is a hen.
     */
    const f = muzzle.front;
    if (f.w >= 4) {
      const y = f.y + Math.max(0, Math.floor(f.h / 2));
      put(px, f.x + 1, y, 0x000000);
      put(px, f.x + f.w - 2, y, 0x000000);
    }
  }

  for (const name of ["legFR", "legFL", "legBR", "legBL"] as const) {
    const leg = layout[name];
    if (leg === undefined) continue;
    /*
     * A bird's leg is scale from the knee down; a beast's is hide with a
     * hoof on the end. So the whole shank is yellow on a курка, and the
     * bottom two rows only on the others.
     */
    if (def.stance === "bird") {
      fillBox(px, leg, pal.hoof);
      continue;
    }
    fillBox(px, leg, pal.hide);
    // The hoof: the bottom two rows of every side, and the sole.
    for (const side of [leg.right, leg.left, leg.front, leg.back]) {
      fill(px, { x: side.x, y: side.y + side.h - 2, w: side.w, h: 2 }, pal.hoof);
    }
    fill(px, leg.bottom, pal.hoof);
  }

  for (const name of ["hornR", "hornL"] as const) {
    const horn = layout[name];
    if (horn === undefined) continue;
    if (def.headgear === "horns") {
      fillBox(px, horn, pal.horn);
      // Dark at the root and pale at the tip, which is how a real horn goes.
      fill(px, { x: horn.front.x, y: horn.front.y, w: 2, h: horn.front.h }, pal.hideDark);
    } else if (def.headgear === "comb") {
      // The гребінь. Both boxes are painted so the pair reads the same from
      // either side, and only one of them is actually hung on the model.
      fillBox(px, horn, pal.horn);
      for (const side of [horn.right, horn.left]) {
        // Notched along the top, which is what makes a comb a comb.
        for (let x = 0; x < side.w; x += 2) {
          put(px, side.x + x, side.y, 0x8e1418);
        }
      }
    } else {
      fillBox(px, horn, pal.hide);
      // A pig's ear: darker inside, and it hangs forward over the eye.
      fill(px, horn.front, pal.muzzle);
    }
  }

  /*
   * The wings: clay, with the flight feathers black at the tips.
   *
   * That black edge is the полтавська глиняста's marking, and it is on the
   * ends of the махові and the кермові and nowhere else - so the wing is
   * dark down its trailing edge and along its bottom, and the body is not.
   */
  for (const name of ["wingR", "wingL"] as const) {
    const wing = layout[name];
    if (wing === undefined) continue;
    fillBox(px, wing, pal.hide);
    for (const side of [wing.right, wing.left]) {
      fill(px, { x: side.x, y: side.y, w: side.w, h: 1 }, pal.hideLit);
      /*
       * One column at the back and nothing else.
       *
       * The black is on the *tips* of the махові and nowhere else. Two rows
       * and two columns of it - which is what this said first - is a third
       * of the wing, and at the size a hen is drawn that is not a marking,
       * it is a crow sitting on her side.
       */
      fill(px, { x: side.x + side.w - 1, y: side.y, w: 1, h: side.h }, pal.hideDark);
    }
  }

  /*
   * The чуб, off the українська чубата.
   *
   * A tuft of the bird's own feathers rather than anything red, and the one
   * thing that breed is named for. It carries no purpose whatever.
   */
  const crest = layout.crest;
  if (crest !== undefined) {
    fillBox(px, crest, pal.hide);
    fill(px, crest.top, pal.hideLit);
    speckle(px, crest.top, pal.hideDark, 0.3);
  }

  // The neck, a shade lighter than the body: on a глиняста it is the palest
  // part of the bird, and it is what separates her head from her back.
  for (const neck of [layout.neck, layout.neckR, layout.neckL]) {
    if (neck === undefined) continue;
    fillBox(px, neck, pal.hide);
    fill(px, neck.top, pal.hideLit);
    fill(px, neck.bottom, pal.belly);
  }
  const neck = layout.neck;
  // Pale, on a hen. A Змій's three are the colour of the rest of him, which
  // the loop above has already given them.
  if (neck !== undefined && def.stance === "bird") {
    fillBox(px, neck, pal.belly);
    fill(px, neck.top, pal.hideLit);
  }

  const tail = layout.tail;
  if (tail !== undefined) {
    if (def.stance === "bird") {
      // Clay, with the black on the ends of the кермові only - the same
      // marking as the wing and for the same reason.
      fillBox(px, tail, pal.hide);
      for (const side of [tail.right, tail.left]) {
        fill(px, { x: side.x, y: side.y, w: side.w, h: 1 }, pal.hideDark);
      }
      fill(px, tail.top, pal.hideDark);
    } else {
      fillBox(px, tail, pal.hideDark);
    }
  }

  return px;
}
